/**
 * slash_line 管理コンソールAPI クライアント。
 *
 * React → slash_admin `/api/line_console/<path>`（session＋is_staff の中継）→ slash_line `/api/console/<path>`。
 * 内部トークンはブラウザに出さず、中継が Bearer を付ける（D23-1）。
 * 認証は admin のセッション Cookie（credentials: "include"）。
 */

const ADMIN_API_BASE = process.env.REACT_APP_API_URL || "";
const RELAY_BASE = `${ADMIN_API_BASE}/api/line_console`;

async function relay(path, { method = "GET", body } = {}) {
  const res = await fetch(`${RELAY_BASE}/${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") sp.append(k, v);
  });
  return sp.toString();
}

// --- セグメント配信 ---
export const lineConsole = {
  segmentOptions: (tenant = "slash") =>
    relay(`segments/options/?tenant=${encodeURIComponent(tenant)}`),

  // 保存前でも「誰に届くか」を返す（会員名つき・最大1000件）
  segmentPreview: (segment, { tenant = "slash", target_tag = "", sample_size = 1000 } = {}) =>
    relay("segments/preview/", {
      method: "POST",
      body: { tenant, segment, target_tag, sample_size },
    }),

  // LINE連携済みの会員を名前（またはLINEの表示名）で探す
  searchMembers: (q, tenant = "slash") =>
    relay(`segments/members/?${qs({ q, tenant })}`),

  listBroadcasts: (tenant = "slash") =>
    relay(`broadcasts/?tenant=${encodeURIComponent(tenant)}`),

  createBroadcast: (payload, tenant = "slash") =>
    relay("broadcasts/", { method: "POST", body: { tenant, ...payload } }),

  getBroadcast: (id) => relay(`broadcasts/${id}/`),

  updateBroadcast: (id, payload) =>
    relay(`broadcasts/${id}/`, { method: "PATCH", body: payload }),

  previewBroadcast: (id) => relay(`broadcasts/${id}/preview/`, { method: "POST" }),

  // 会員(member_ref)を指定して1通だけ送る（LINE の userId はスタッフには分からないため）
  testSend: (id, memberRef) =>
    relay(`broadcasts/${id}/test-send/`, {
      method: "POST",
      body: { member_ref: memberRef },
    }),

  // sendNow=true で「今すぐ送信」（毎分の配信処理が次の回で送る）
  schedule: (id, { sendNow = false } = {}) =>
    relay(`broadcasts/${id}/schedule/`, { method: "POST", body: { send_now: sendNow } }),

  cancel: (id) => relay(`broadcasts/${id}/cancel/`, { method: "POST" }),

  // --- シナリオ管理（Phase4）---
  scenarioMeta: () => relay("scenarios/meta/"),

  listScenarios: (tenant = "slash") =>
    relay(`scenarios/?tenant=${encodeURIComponent(tenant)}`),

  getScenario: (sid, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/?tenant=${encodeURIComponent(tenant)}`),

  updateScenario: (sid, payload, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/`, {
      method: "PATCH",
      body: { tenant, ...payload },
    }),

  createScenario: (payload, tenant = "slash") =>
    relay("scenarios/", { method: "POST", body: { tenant, ...payload } }),

  deleteScenario: (sid, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/`, { method: "DELETE", body: { tenant } }),

  // draft を渡すと**未保存の編集内容**で描画する（渡さなければ保存済みの内容）
  previewScenario: (sid, variant = "a", draft = null, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/preview/`, {
      method: "POST",
      body: { tenant, variant, ...(draft || {}) },
    }),

  runScenarioNow: (sid, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/run-now/`, {
      method: "POST",
      body: { tenant },
    }),

  scenarioAudits: (sid) => relay(`scenarios/${encodeURIComponent(sid)}/audits/`),

  rollbackScenario: (sid, auditId, tenant = "slash") =>
    relay(`scenarios/${encodeURIComponent(sid)}/rollback/`, {
      method: "POST",
      body: { tenant, audit_id: auditId },
    }),

  // --- 分析（D34）---
  // period は { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }
  analyticsOverview: (period, tenant = "slash") =>
    relay(`analytics/overview/?${qs({ ...period, tenant })}`),

  analyticsScenarios: (period, tenant = "slash") =>
    relay(`analytics/scenarios/?${qs({ ...period, tenant })}`),

  analyticsLinkMembers: (tenant = "slash") =>
    relay(`analytics/link-members/?${qs({ tenant })}`),

  analyticsUsers: (params = {}, tenant = "slash") =>
    relay(`analytics/users/?${qs({ ...params, tenant })}`),

  analyticsUserTimeline: (userId, tenant = "slash") =>
    relay(`analytics/users/${userId}/timeline/?${qs({ tenant })}`),
};

export default lineConsole;
